import { useNavigate } from "@tanstack/react-router"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"
import { isApiError } from "@/api/client"
import type { AgentSummary } from "@/api/generated/AgentSummary"
import { describeError } from "@/api/mutation"
import { useArchiveAgent, useDeleteAgent, useDuplicateAgent } from "@/api/queries/agents"
import { useConfirm } from "@/components/app/confirm-dialog"

type AgentRef = Pick<AgentSummary, "id" | "name" | "archived_at">

/**
 * Duplicate / archive / delete with confirmations. Deleting an agent that is
 * still used (409, e.g. a team member) explains why and offers archiving.
 */
export function useAgentActions() {
  const { t } = useTranslation()
  const confirm = useConfirm()
  const navigate = useNavigate()
  const duplicate = useDuplicateAgent()
  const archive = useArchiveAgent()
  const remove = useDeleteAgent()

  const setArchived = (a: AgentRef, value: boolean) =>
    archive.mutateAsync({ id: a.id, name: a.name, archive: value })

  return {
    duplicate: async (a: AgentRef, open = true) => {
      const copy = await duplicate.mutateAsync(a)
      if (open) void navigate({ to: "/studio/agents/$agentId", params: { agentId: String(copy.id) } })
      return copy
    },
    archive: (a: AgentRef) =>
      confirm({
        title: t("studio.agents.archive.title", { name: a.name }),
        description: t("studio.agents.archive.description"),
        confirmLabel: t("studio.agents.archive.confirm"),
        onConfirm: () => setArchived(a, true),
      }),
    unarchive: (a: AgentRef) => setArchived(a, false),
    remove: (a: AgentRef, onDeleted?: () => void) =>
      confirm({
        title: t("studio.agents.delete.title", { name: a.name }),
        description: t("studio.agents.delete.description"),
        confirmLabel: t("studio.agents.delete.confirm"),
        confirmText: a.name,
        destructive: true,
        onConfirm: async () => {
          try {
            await remove.mutateAsync(a)
            onDeleted?.()
          } catch (e) {
            if (isApiError(e) && e.isConflict) {
              // let the first dialog close, then explain
              setTimeout(
                () =>
                  void confirm({
                    title: t("studio.agents.delete.blockedTitle", { name: a.name }),
                    description: e.message,
                    confirmLabel: a.archived_at
                      ? t("common.confirm")
                      : t("studio.agents.delete.archiveInstead"),
                    onConfirm: a.archived_at ? undefined : () => setArchived(a, true),
                  }),
                150,
              )
              return
            }
            toast.error(describeError(e, t))
            throw e
          }
        },
      }),
  }
}
